import React, {
  useRef,
  useState,
} from 'react';
import EditIcon from 'core/icons/edit';
import IconButton from '@mui/material/IconButton';
import clsx from 'clsx';
import {
  formatPhone,
  notEmptyString,
} from '@populateui/utils';
import ToolTip from 'core/components/Tooltip';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Spinner from 'pages/Dashboard/components/Spinner';
import ProviderContactConfigDialog from 'pages/Dashboard/components/ReferringProvider/ProviderContactConfigDialog';
import {
  useGetProviderEntryConfig,
} from 'pages/Dashboard/components/ReferringProvider/hooks';
import {
  ProviderEntryConfigTarget,
} from 'pages/Dashboard/components/ReferringProvider/providerEntryConfigTarget';

type Props = {
  target: ProviderEntryConfigTarget;
  className?: string;
  text?: string;
  shouldStackContact?: boolean;
};

type ContactValueProps = {
  label: string;
  value: string;
  shouldStackContact?: boolean;
};

function formatContactPhoneNumber(value: string = '') {
  if (!notEmptyString(value)) {
    return '--';
  }

  const digits = value.replace(/\D/g, '');
  const normalized = digits.length === 11
    ? `+${digits}`
    : value;

  return formatPhone(normalized);
}

function ContactValue({
  label,
  value,
  shouldStackContact = false,
}: ContactValueProps) {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div
      className={clsx('flex min-w-[10rem] items-center gap-1', {
        'pl-0': shouldStackContact,
        'pl-4 first:pl-0 last:pr-0': !shouldStackContact,
      })}
    >
      <span className="text-xs font-medium text-gray-500">
        {label}
        :
      </span>
      <ToolTip title={value} auto ref={ref}>
        <span
          className="min-w-0 truncate text-xs font-semibold text-gray-700"
          ref={ref}
        >
          {value}
        </span>
      </ToolTip>
    </div>
  );
}

export default function CompactContactEditor({
  target,
  className,
  text = 'Primary Care Physician',
  shouldStackContact = false,
}: Props) {
  const [contactOpen, setContactOpen] = useState(false);

  const {
    data: providerConfig,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetProviderEntryConfig(target);

  const phoneNumber = providerConfig?.phoneNumber?.phoneNumberWithAreaCode;
  const faxNumber = providerConfig?.faxNumber;
  const email = providerConfig?.email;

  const contactRows = [
    {
      label: 'Phone',
      value: formatContactPhoneNumber(phoneNumber ?? '--'),
    },
    {
      label: 'Fax',
      value: formatContactPhoneNumber(faxNumber ?? '--'),
    },
    {
      label: 'Email',
      value: notEmptyString(email) ? email : '—',
    },
  ];

  return (
    <>
      <Wrapper
        id="pcp-contacts"
        label="Contacts"
        className="min-w-[14rem]"
      >
        <div
          className={clsx('flex min-w-0 justify-between gap-6 rounded border border-gray-200 bg-white px-3 py-2', {
            'relative flex-col items-stretch': shouldStackContact,
          }, className)}
        >
          {(isLoading || isFetching) && (
            <div className={clsx('flex items-center gap-2 text-xs text-gray-500', {
              'pr-7': shouldStackContact,
              'pr-2': !shouldStackContact,
            })}
            >
              <Spinner />
              Loading contacts...
            </div>
          )}
          {!isLoading && !isFetching && isError && (
            <div
              className={clsx('text-xs text-red-600', {
                'pr-7': shouldStackContact,
                'pr-2': !shouldStackContact,
              })}
              title={error instanceof Error ? error.message : undefined}
            >
              Failed to load contacts.
            </div>
          )}
          {!isLoading && !isFetching && !isError && (
            <div
              className={clsx('flex min-w-0 flex-1 overflow-hidden', {
                'flex-col gap-2 pr-7': shouldStackContact,
                'gap-4 divide-x divide-gray-200': !shouldStackContact,
              })}
            >
              {contactRows.map(({ label, value }) => (
                <ContactValue
                  key={label}
                  label={label}
                  value={value ?? '--'}
                  shouldStackContact={shouldStackContact}
                />
              ))}
            </div>
          )}
          <div className={clsx('flex shrink-0 items-center justify-end gap-3', {
            'absolute right-2 top-2': shouldStackContact,
          })}
          >
            <ToolTip title={`Edit ${text} contacts`}>
              <IconButton
                className="!p-0"
                aria-label={`Edit ${text} contacts`}
                onClick={() => { setContactOpen(true); }}
              >
                <EditIcon className="!h-5 !w-5 !text-black" />
              </IconButton>
            </ToolTip>
          </div>
        </div>
      </Wrapper>
      {contactOpen && (
        <ProviderContactConfigDialog
          target={target}
          onClose={() => { setContactOpen(false); }}
        />
      )}
    </>
  );
}
